import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Descriptions, Empty, Form, Input, Popconfirm, Radio, Select, Space, Spin, Tag, Typography, Upload, message } from 'antd';
import { CloseCircleOutlined, EyeOutlined, FilePdfOutlined, ReloadOutlined, SendOutlined, UploadOutlined } from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import { View } from '../constants/roles';
import { useApiToken } from '../hooks/useApiToken';
import { apiRequest, listMyCredentials, listProviders } from '../utils/integrationsApi';
import { impersonationHeaders, getActiveImpersonation } from '../utils/impersonationFetch';
import { openServerUrl, preopenWindow, userMessageForError } from '../utils/apiError';
import { reportOpenFailure } from '../utils/openLinkFeedback';
import { formatDdMmYyyy } from '../utils/formatDdMmYyyy';

const { Text } = Typography;

/**
 * Contract signing for the project Contract tab. Backend: contractSigning.routes.ts
 * (Integrations module, .claude/design/integrations-module.md).
 *
 * Two real paths, chosen by the Lead Advisor:
 *   - ESIGN_PROVIDER: offered only for providers the server lists as ESIGN (GET
 *     /me/integrations/providers) for which the Lead Advisor has a stored credential. Flow:
 *     create (DRAFT) -> send the contract PDF + signer (SENT) -> the provider's verified webhook
 *     completes it (SIGNED/DECLINED/EXPIRED); the panel shows server status on Refresh.
 *   - MANUAL_UPLOAD: upload the countersigned PDF; the server records SIGNED.
 * Status is whatever the server returns; nothing is computed locally.
 *
 * `canWrite` is a UX hint only: the API gates writes to Lead Advisor / platform_admin via the
 * effective role. While impersonating, write controls are disabled rather than 403-ing. Contract
 * routes honour X-Impersonation-Id, so that header is forwarded.
 */
const STATUS_META = {
    DRAFT: { color: 'gold', label: 'Draft' },
    SENT: { color: 'blue', label: 'Sent for signature' },
    SIGNED: { color: 'green', label: 'Signed' },
    DECLINED: { color: 'red', label: 'Declined' },
    EXPIRED: { color: 'default', label: 'Expired' },
    CANCELLED: { color: 'default', label: 'Cancelled' },
};
const ACTIVE = ['DRAFT', 'SENT'];
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const methodLabel = (r, providerName) =>
    r.method === 'MANUAL_UPLOAD' ? 'Signed PDF upload' : `E-sign via ${providerName(r.provider)}`;

function checkPdfSize(file) {
    if (file && file.size > MAX_UPLOAD_BYTES) {
        message.error(`${file.name || 'This file'} is ${(file.size / 1024 / 1024).toFixed(1)} MB. The maximum is 20 MB.`);
        return false;
    }
    return true;
}

const ContractSigningPanel = ({ projectId, canWrite: canWriteByRole }) => {
    const { setCurrentView } = useApp();
    const getToken = useApiToken();
    const [uploadForm] = Form.useForm();
    const [sendForm] = Form.useForm();
    const [state, setState] = useState({ phase: 'loading', requests: [], error: null });
    const [esign, setEsign] = useState({ providers: [], credentials: [] }); // configured e-sign options
    const [busy, setBusy] = useState(null);
    const [uploadFiles, setUploadFiles] = useState([]);
    const [sendFiles, setSendFiles] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [method, setMethod] = useState('MANUAL_UPLOAD');
    const [credentialId, setCredentialId] = useState(undefined);

    // The API denies write actions to an impersonating Lead Advisor (effective role); disable instead of 403-ing.
    const impersonating = !!getActiveImpersonation();
    const canWrite = canWriteByRole && !impersonating;

    const base = `/projects/${encodeURIComponent(projectId)}/contract-signing`;
    const req = useCallback(
        (path, opts = {}) => apiRequest(getToken, `${base}${path}`, { ...opts, headers: { ...impersonationHeaders(), ...(opts.headers || {}) } }),
        [getToken, base],
    );

    const load = useCallback(async () => {
        setState((s) => ({ ...s, phase: 'loading', error: null }));
        try {
            const data = await req('');
            setState({ phase: 'ready', requests: data?.contractSigningRequests ?? [], error: null });
        } catch (err) {
            setState({ phase: 'error', requests: [], error: userMessageForError(err, 'Could not load signing status.') });
        }
    }, [req]);

    useEffect(() => {
        if (projectId) load();
    }, [projectId, load]);

    // Provider catalogue + the Lead Advisor's own credentials (only the Lead Advisor may read them).
    useEffect(() => {
        if (!canWriteByRole) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const [providers, credentials] = await Promise.all([listProviders(getToken), listMyCredentials(getToken)]);
                if (!cancelled) setEsign({ providers: providers.filter((p) => p.kind === 'ESIGN'), credentials });
            } catch {
                if (!cancelled) setEsign({ providers: [], credentials: [] }); // platform_admin or transient: manual upload still works
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [canWriteByRole, getToken]);

    const providerName = (id) => esign.providers.find((p) => p.providerId === id)?.displayName || id;
    const esignOptions = esign.credentials
        .filter((c) => esign.providers.some((p) => p.providerId === c.provider))
        .map((c) => ({ value: c.id, label: `${providerName(c.provider)}${c.displayLabel ? ` - ${c.displayLabel}` : ''}`, provider: c.provider }));

    const run = async (key, fn, okMsg) => {
        setBusy(key);
        try {
            await fn();
            if (okMsg) message.success(okMsg);
            await load();
            return true;
        } catch (err) {
            message.error(userMessageForError(err, 'Action failed.'));
            await load(); // the server state may have moved (e.g. a terminal status); show it
            return false;
        } finally {
            setBusy(null);
        }
    };

    const start = () => {
        if (method === 'ESIGN_PROVIDER') {
            const opt = esignOptions.find((o) => o.value === credentialId);
            if (!opt) {
                message.warning('Choose a configured e-sign provider.');
                return undefined;
            }
            return run('start', () => req('', { method: 'POST', body: { method: 'ESIGN_PROVIDER', provider: opt.provider, credentialId: opt.value } }), 'Signing request created.');
        }
        return run('start', () => req('', { method: 'POST', body: { method: 'MANUAL_UPLOAD' } }), 'Signing request started.');
    };

    const rawFile = (list) => list[0]?.originFileObj || list[0];
    const onPick = (setter) => ({ fileList: next }) => {
        const f = next[next.length - 1];
        if (f && !checkPdfSize(f.originFileObj || f)) {
            setter([]);
            return;
        }
        setter(next.slice(-1));
    };

    const upload = async () => {
        let values;
        try {
            values = await uploadForm.validateFields();
        } catch {
            return;
        }
        const file = rawFile(uploadFiles);
        if (!file) {
            message.warning('Choose the signed PDF to upload.');
            return;
        }
        if (!checkPdfSize(file)) return;
        const fd = new FormData();
        fd.append('file', file);
        fd.append('signedByName', values.signedByName.trim());
        if (values.signedByEmail?.trim()) fd.append('signedByEmail', values.signedByEmail.trim());
        const ok = await run('upload', () => req(`/${encodeURIComponent(state.requests[0].id)}/signed-document`, { method: 'POST', formData: fd }), 'Signed PDF uploaded. Contract marked signed.');
        if (ok) {
            uploadForm.resetFields();
            setUploadFiles([]);
        }
    };

    const send = async () => {
        let values;
        try {
            values = await sendForm.validateFields();
        } catch {
            return;
        }
        const file = rawFile(sendFiles);
        if (!file) {
            message.warning('Choose the contract PDF to send for signature.');
            return;
        }
        if (!checkPdfSize(file)) return;
        const fd = new FormData();
        fd.append('file', file);
        fd.append('signerName', values.signerName.trim());
        fd.append('signerEmail', values.signerEmail.trim());
        const ok = await run('send', () => req(`/${encodeURIComponent(state.requests[0].id)}/send`, { method: 'POST', formData: fd }), 'Sent for signature.');
        if (ok) {
            sendForm.resetFields();
            setSendFiles([]);
        }
    };

    const cancel = (id) => run('cancel', () => req(`/${encodeURIComponent(id)}/cancel`, { method: 'POST' }), 'Signing request cancelled.');

    const viewSigned = async (id) => {
        setBusy('view');
        // Open synchronously so popup blockers allow it, then point it at the SAS URL.
        const win = preopenWindow();
        try {
            const data = await req(`/${encodeURIComponent(id)}/signed-document`);
            if (!data?.url) {
                if (win && !win.closed) win.close();
                message.error('No signed document is available.');
            } else if (!openServerUrl(data.url, win)) {
                reportOpenFailure(message, data.url);
            }
        } catch (err) {
            if (win && !win.closed) win.close();
            message.error(userMessageForError(err, 'Could not open the signed PDF.'));
        } finally {
            setBusy(null);
        }
    };

    const latest = state.requests[0] || null;
    const isActive = !!latest && ACTIVE.includes(latest.status);
    const meta = latest ? STATUS_META[latest.status] || { color: 'default', label: latest.status } : null;
    const cancelButton = (
        <Popconfirm
            title="Cancel this signing request?"
            description={latest?.status === 'SENT' ? 'The e-sign provider is asked to recall it first.' : undefined}
            okText="Cancel request"
            okButtonProps={{ danger: true }}
            onConfirm={() => cancel(latest.id)}
        >
            <Button danger icon={<CloseCircleOutlined />} loading={busy === 'cancel'}>Cancel request</Button>
        </Popconfirm>
    );

    const startControls = canWrite && (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {esignOptions.length > 0 ? (
                <Radio.Group value={method} onChange={(e) => setMethod(e.target.value)}>
                    <Radio value="MANUAL_UPLOAD">Upload signed PDF</Radio>
                    <Radio value="ESIGN_PROVIDER">Send via e-sign provider</Radio>
                </Radio.Group>
            ) : esign.providers.length > 0 ? (
                <Text type="secondary">
                    To send for e-signature, configure {esign.providers.map((p) => p.displayName).join(' / ')} in{' '}
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setCurrentView(View.INTEGRATIONS)}>Integrations</Button>.
                </Text>
            ) : null}
            {method === 'ESIGN_PROVIDER' && esignOptions.length > 0 && (
                <Select style={{ minWidth: 280 }} placeholder="E-sign provider" options={esignOptions} value={credentialId} onChange={setCredentialId} />
            )}
            <Button type="primary" icon={<FilePdfOutlined />} loading={busy === 'start'} onClick={start}>
                {method === 'ESIGN_PROVIDER' ? 'Start e-sign request' : 'Start signing (upload signed PDF)'}
            </Button>
        </Space>
    );

    return (
        <Card
            title="Contract signing"
            extra={<Button size="small" icon={<ReloadOutlined />} onClick={load} loading={state.phase === 'loading'}>Refresh</Button>}
        >
            {state.phase === 'loading' && !latest && <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>}

            {canWriteByRole && impersonating && (
                <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Signing actions are disabled while you are impersonating another user." />
            )}

            {state.phase === 'error' && <Alert type="error" showIcon message="Could not load signing status" description={state.error} />}

            {state.phase === 'ready' && !latest && (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={canWrite ? 'No signing request yet. Choose how this contract will be signed.' : 'The Lead Advisor has not started contract signing yet.'}
                >
                    {startControls}
                </Empty>
            )}

            {latest && (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Descriptions size="small" column={1}>
                        <Descriptions.Item label="Status"><Tag color={meta.color}>{meta.label}</Tag></Descriptions.Item>
                        <Descriptions.Item label="Method">{methodLabel(latest, providerName)}</Descriptions.Item>
                        <Descriptions.Item label="Signing attempt">#{latest.contractDraftVersion}</Descriptions.Item>
                        <Descriptions.Item label="Started">{formatDdMmYyyy(latest.createdAt)}</Descriptions.Item>
                        {latest.status === 'SENT' && (
                            <Descriptions.Item label="Next">Waiting for the signer. Completion arrives from the provider; use Refresh to see it.</Descriptions.Item>
                        )}
                        {latest.status === 'SIGNED' && (
                            <>
                                <Descriptions.Item label="Signed by">{latest.signedByName || '-'}{latest.signedByEmail ? ` (${latest.signedByEmail})` : ''}</Descriptions.Item>
                                <Descriptions.Item label="Signed on">{formatDdMmYyyy(latest.signedAt)}</Descriptions.Item>
                            </>
                        )}
                    </Descriptions>

                    {latest.status === 'SIGNED' && latest.signedDocumentBlobUrl && (
                        <Button icon={<EyeOutlined />} loading={busy === 'view'} onClick={() => viewSigned(latest.id)}>View signed PDF</Button>
                    )}

                    {latest.status === 'DRAFT' && canWrite && latest.method === 'MANUAL_UPLOAD' && (
                        <Form form={uploadForm} layout="vertical" autoComplete="off" requiredMark={false}>
                            <Form.Item name="signedByName" label="Signed by (name)" rules={[{ required: true, whitespace: true, message: 'Signatory name is required.' }]}>
                                <Input maxLength={200} />
                            </Form.Item>
                            <Form.Item name="signedByEmail" label="Signatory email (optional)">
                                <Input maxLength={200} />
                            </Form.Item>
                            <Form.Item label="Signed PDF">
                                <Upload accept="application/pdf,.pdf" maxCount={1} fileList={uploadFiles} beforeUpload={() => false} onChange={onPick(setUploadFiles)}>
                                    <Button icon={<UploadOutlined />}>Choose PDF</Button>
                                </Upload>
                                <Text type="secondary" style={{ fontSize: 12 }}>PDF only, up to 20 MB.</Text>
                            </Form.Item>
                            <Space wrap>
                                <Popconfirm
                                    title="Mark this contract as signed?"
                                    description="The uploaded PDF becomes the signed record. This cannot be undone."
                                    okText="Upload and mark signed"
                                    open={confirmOpen}
                                    onOpenChange={(o) => { if (!o) setConfirmOpen(false); }}
                                    onConfirm={() => { setConfirmOpen(false); return upload(); }}
                                >
                                    <Button
                                        type="primary"
                                        icon={<UploadOutlined />}
                                        loading={busy === 'upload'}
                                        onClick={async () => {
                                            // Validate before asking "are you sure": never confirm an action that will abort.
                                            try { await uploadForm.validateFields(); } catch { return; }
                                            if (!uploadFiles[0]) { message.warning('Choose the signed PDF to upload.'); return; }
                                            setConfirmOpen(true);
                                        }}
                                    >
                                        Upload signed PDF
                                    </Button>
                                </Popconfirm>
                                {cancelButton}
                            </Space>
                        </Form>
                    )}

                    {latest.status === 'DRAFT' && canWrite && latest.method === 'ESIGN_PROVIDER' && (
                        <Form form={sendForm} layout="vertical" autoComplete="off" requiredMark={false}>
                            <Form.Item name="signerName" label="Signer name" rules={[{ required: true, whitespace: true, message: 'Signer name is required.' }]}>
                                <Input maxLength={200} />
                            </Form.Item>
                            <Form.Item
                                name="signerEmail"
                                label="Signer email"
                                rules={[{ required: true, message: 'Signer email is required.' }, { pattern: EMAIL_SHAPE, message: 'Enter a valid email address.' }]}
                            >
                                <Input maxLength={320} />
                            </Form.Item>
                            <Form.Item label="Contract PDF to be signed">
                                <Upload accept="application/pdf,.pdf" maxCount={1} fileList={sendFiles} beforeUpload={() => false} onChange={onPick(setSendFiles)}>
                                    <Button icon={<UploadOutlined />}>Choose PDF</Button>
                                </Upload>
                                <Text type="secondary" style={{ fontSize: 12 }}>PDF only, up to 20 MB. The exact file sent is recorded in the audit log.</Text>
                            </Form.Item>
                            <Space wrap>
                                <Button type="primary" icon={<SendOutlined />} loading={busy === 'send'} onClick={send}>Send for signature</Button>
                                {cancelButton}
                            </Space>
                        </Form>
                    )}

                    {latest.status === 'SENT' && canWrite && <Space>{cancelButton}</Space>}

                    {!isActive && latest.status !== 'SIGNED' && startControls}
                </Space>
            )}
        </Card>
    );
};

export default ContractSigningPanel;
